import type { FixedRoute, FixedRouteStop, FixedRouteStopConfig } from '../../types/fixedRoutes';
import { calculateTravelTimesAndDistancesBatch as osrmCalculateTravelTimesAndDistancesBatch } from '../../services/routing/osrmClient';
import { calculateTravelTimesAndDistancesBatch as hereCalculateTravelTimesAndDistancesBatch } from '../../services/routing/hereClient';

export interface OptimizationResult {
  optimizedStopConfigs: FixedRouteStopConfig[];
  originalDistance: number;
  optimizedDistance: number;
  distanceSaved: number;
  savingsPercent: string;
  originalOrder: string[];
  optimizedOrder: string[];
}

/**
 * Optimize route order to minimize total distance traveled
 * Uses Nearest Neighbor heuristic for TSP optimization with 2-opt improvement
 */
export async function optimizeRoute(
  route: FixedRoute,
  stops: FixedRouteStop[]
): Promise<OptimizationResult> {
  // Map stop configs to actual stops and validate
  const routeStops = route.stops
    .map((stopConfig) => {
      const stop = stops.find((s) => s.id === stopConfig.stopId);
      if (!stop) {
        console.warn(`[optimizeRoute] Stop ${stopConfig.stopId} not found in stops array`);
        return null;
      }
      return stop;
    })
    .filter((stop): stop is FixedRouteStop => {
      if (!stop || !stop.coordinates) {
        if (stop) {
          console.warn(`[optimizeRoute] Filtering out stop ${stop.id} - missing coordinates`);
        }
        return false;
      }
      
      // Validate coordinates are valid numbers
      const lat = stop.coordinates.lat;
      const lng = stop.coordinates.lng;
      
      if (typeof lat !== 'number' || typeof lng !== 'number' ||
          isNaN(lat) || isNaN(lng) ||
          !isFinite(lat) || !isFinite(lng)) {
        console.warn(`[optimizeRoute] Filtering out stop ${stop.id} (${stop.name}) with invalid coordinates:`, stop.coordinates);
        return false;
      }
      
      // Validate coordinate ranges
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        console.warn(`[optimizeRoute] Filtering out stop ${stop.id} (${stop.name}) with coordinates out of range:`, stop.coordinates);
        return false;
      }
      
      return true;
    });

  if (routeStops.length < 2) {
    const missingStops = route.stops.length - routeStops.length;
    throw new Error(`Route must have at least 2 stops with valid coordinates. Found ${routeStops.length} valid stops out of ${route.stops.length} total${missingStops > 0 ? ` (${missingStops} stops filtered out)` : ''}.`);
  }
  
  console.log(`[optimizeRoute] Processing ${routeStops.length} stops for optimization:`, 
    routeStops.map(s => ({ id: s.id, name: s.name, coords: s.coordinates }))
  );

  // Distance/time cache
  const distanceTimeCache = new Map<string, { distanceMeters: number; travelTimeMinutes: number }>();
  const getCacheKey = (fromStopId: string, toStopId: string) => `${fromStopId}-${toStopId}`;

  // Create all possible pairs for distance matrix
  const pairsToCalculate: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop; fromIndex: number; toIndex: number }> = [];
  
  for (let i = 0; i < routeStops.length; i++) {
    for (let j = 0; j < routeStops.length; j++) {
      if (i !== j) {
        const cacheKey = getCacheKey(routeStops[i].id, routeStops[j].id);
        if (!distanceTimeCache.has(cacheKey)) {
          pairsToCalculate.push({ fromStop: routeStops[i], toStop: routeStops[j], fromIndex: i, toIndex: j });
        }
      }
    }
  }

  // Calculate distances for pairs not in cache
  // Use the same routing service as RouteBuilder: HERE for bus routes, OSRM for regular routes
  if (pairsToCalculate.length > 0) {
    let distanceResults: Array<{ travelTimeMinutes: number; distanceMeters: number }>;
    
    if (route.isBusRoute) {
      // Use HERE for bus routes (same as RouteBuilder)
      console.log(`[optimizeRoute] Using HERE routing for bus route (${pairsToCalculate.length} pairs)`);
      distanceResults = await hereCalculateTravelTimesAndDistancesBatch(
        pairsToCalculate.map((p) => ({ fromStop: p.fromStop, toStop: p.toStop })),
        true
      );
    } else {
      // Use OSRM for regular routes (same as RouteBuilder)
      console.log(`[optimizeRoute] Using OSRM routing for regular route (${pairsToCalculate.length} pairs)`);
      distanceResults = await osrmCalculateTravelTimesAndDistancesBatch(
        pairsToCalculate.map((p) => ({ fromStop: p.fromStop, toStop: p.toStop }))
      );
    }

    // Store results in cache
    distanceResults.forEach((result, index) => {
      const pair = pairsToCalculate[index];
      const cacheKey = getCacheKey(pair.fromStop.id, pair.toStop.id);
      distanceTimeCache.set(cacheKey, result);
    });
  }

  // Build distance matrix: distances[fromIndex][toIndex] = distance in meters
  const distances: number[][] = [];
  for (let i = 0; i < routeStops.length; i++) {
    distances[i] = [];
    for (let j = 0; j < routeStops.length; j++) {
      if (i === j) {
        distances[i][j] = 0;
      } else {
        const cacheKey = getCacheKey(routeStops[i].id, routeStops[j].id);
        const cached = distanceTimeCache.get(cacheKey);
        distances[i][j] = cached?.distanceMeters || 0;
      }
    }
  }

  // Improved Nearest Neighbor TSP heuristic
  // Try starting from each stop and pick the best overall solution
  let bestOrder: number[] = [];
  let bestDistance = Infinity;

  // Try each stop as a starting point
  for (let startIndex = 0; startIndex < routeStops.length; startIndex++) {
    const candidateOrder: number[] = [startIndex];
    const visited = new Set<number>([startIndex]);
    let currentIndex = startIndex;

    // Build route using nearest neighbor from this starting point
    for (let i = 1; i < routeStops.length; i++) {
      let nearestIndex = -1;
      let nearestDistance = Infinity;

      // Find nearest unvisited stop
      for (let j = 0; j < routeStops.length; j++) {
        if (!visited.has(j) && distances[currentIndex][j] < nearestDistance) {
          nearestDistance = distances[currentIndex][j];
          nearestIndex = j;
        }
      }

      if (nearestIndex >= 0) {
        candidateOrder.push(nearestIndex);
        visited.add(nearestIndex);
        currentIndex = nearestIndex;
      }
    }

    // Calculate total distance for this candidate route
    const candidateDistance = candidateOrder.reduce((total, index, i) => {
      if (i < candidateOrder.length - 1) {
        const nextIndex = candidateOrder[i + 1];
        return total + distances[index][nextIndex];
      }
      return total;
    }, 0);

    // Keep track of the best route found so far
    if (candidateDistance < bestDistance) {
      bestDistance = candidateDistance;
      bestOrder = [...candidateOrder];
    }
  }

  // Keep first stop fixed
  const keepFirstStopFixed = true;
  let optimizedOrder = keepFirstStopFixed 
    ? (() => {
        // Start from first stop but use the improved nearest neighbor
        const fixedStartOrder: number[] = [0];
        const fixedVisited = new Set<number>([0]);
        let currentIdx = 0;

        for (let i = 1; i < routeStops.length; i++) {
          let nearestIdx = -1;
          let nearestDist = Infinity;

          for (let j = 0; j < routeStops.length; j++) {
            if (!fixedVisited.has(j) && distances[currentIdx][j] < nearestDist) {
              nearestDist = distances[currentIdx][j];
              nearestIdx = j;
            }
          }

          if (nearestIdx >= 0) {
            fixedStartOrder.push(nearestIdx);
            fixedVisited.add(nearestIdx);
            currentIdx = nearestIdx;
          }
        }

        // Check if keeping first stop fixed gives a better result than the global best
        const fixedStartDistance = fixedStartOrder.reduce((total, index, i) => {
          if (i < fixedStartOrder.length - 1) {
            return total + distances[index][fixedStartOrder[i + 1]];
          }
          return total;
        }, 0);

        // Use whichever is better: global best or fixed-start
        return fixedStartDistance < bestDistance ? fixedStartOrder : bestOrder;
      })()
    : bestOrder;

  // Apply 2-opt improvement to the route
  const improveWith2Opt = (order: number[]): number[] => {
    let improvedOrder = [...order];
    let improved = true;
    let iterations = 0;
    const maxIterations = 100; // Prevent infinite loops

    while (improved && iterations < maxIterations) {
      improved = false;
      iterations++;

      for (let i = 0; i < improvedOrder.length - 2; i++) {
        for (let j = i + 2; j < improvedOrder.length; j++) {
          // Calculate current distance for edges (i, i+1) and (j, j+1)
          const currentDistance = 
            distances[improvedOrder[i]][improvedOrder[i + 1]] +
            (j + 1 < improvedOrder.length ? distances[improvedOrder[j]][improvedOrder[j + 1]] : 0);

          // Calculate distance if we reverse the segment between i+1 and j
          const newDistance = 
            distances[improvedOrder[i]][improvedOrder[j]] +
            (j + 1 < improvedOrder.length ? distances[improvedOrder[i + 1]][improvedOrder[j + 1]] : 0);

          // If reversing this segment improves the route, do it
          if (newDistance < currentDistance) {
            // Reverse segment from i+1 to j
            const reversed = improvedOrder.slice(i + 1, j + 1).reverse();
            improvedOrder = [
              ...improvedOrder.slice(0, i + 1),
              ...reversed,
              ...improvedOrder.slice(j + 1)
            ];
            improved = true;
            break; // Restart the improvement process
          }
        }
        if (improved) break;
      }
    }

    return improvedOrder;
  };

  // Apply 2-opt improvement if we have more than 3 stops
  if (routeStops.length > 3) {
    optimizedOrder = improveWith2Opt(optimizedOrder);
  }

  // Reorder stops based on optimized order
  const optimizedStopConfigs = optimizedOrder.map((index) => route.stops[index]);

  // Calculate total distance savings
  // Original distance: sum of distances in current order
  let originalTotalDistance = 0;
  for (let i = 0; i < route.stops.length - 1; i++) {
    const currentStop = routeStops.find((rs) => rs.id === route.stops[i].stopId);
    const nextStop = routeStops.find((rs) => rs.id === route.stops[i + 1].stopId);
    if (currentStop && nextStop) {
      const currentIndex = routeStops.indexOf(currentStop);
      const nextIndex = routeStops.indexOf(nextStop);
      if (currentIndex >= 0 && nextIndex >= 0) {
        originalTotalDistance += distances[currentIndex][nextIndex];
      }
    }
  }

  const optimizedDistance = optimizedOrder.reduce((total, index, i) => {
    if (i < optimizedOrder.length - 1) {
      const nextIndex = optimizedOrder[i + 1];
      return total + distances[index][nextIndex];
    }
    return total;
  }, 0);

  const distanceSaved = originalTotalDistance - optimizedDistance;
  const savingsPercent = originalTotalDistance > 0 ? ((distanceSaved / originalTotalDistance) * 100).toFixed(1) : '0';

  // Get stop names for display
  const originalOrderNames = route.stops.map((sc) => {
    const stop = stops.find((s) => s.id === sc.stopId);
    return stop?.name || sc.stopId;
  });
  const optimizedOrderNames = optimizedStopConfigs.map((sc) => {
    const stop = stops.find((s) => s.id === sc.stopId);
    return stop?.name || sc.stopId;
  });

  return {
    optimizedStopConfigs,
    originalDistance: originalTotalDistance,
    optimizedDistance: optimizedDistance,
    distanceSaved: distanceSaved,
    savingsPercent: savingsPercent,
    originalOrder: originalOrderNames,
    optimizedOrder: optimizedOrderNames,
  };
}

